import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useAuth } from '@/lib/AuthContext';
import { useCart } from '@/lib/CartContext';
import { getCollectible, getCollectiblePrice, removeFromCart, lockCollectiblePrice, initiateCheckout, getPaymentConfig, createPaymentOrder, verifyPayment, cancelPaymentOrder, getPublicSettings } from '@/lib/api';
import { loadRazorpayCheckout } from '@/lib/razorpayCheckout';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Trash2, CreditCard, ShoppingBag, ArrowRight, Clock, AlertTriangle, AlertCircle } from 'lucide-react';
import { formatInr, MONEY_CLASSNAME } from '@/lib/money';

export default function ChestPage() {
  const { user } = useAuth();
  const { cart, refreshCart } = useCart();
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removingId, setRemovingId] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutBreakdown, setCheckoutBreakdown] = useState(null);
  const [priceLockExpiry, setPriceLockExpiry] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [priceExpired, setPriceExpired] = useState(false);
  const [cancellationPolicy, setCancellationPolicy] = useState('');
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  const [liveRatesAvailable, setLiveRatesAvailable] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const deliveryFormRef = useRef(null);
  const [delivery, setDelivery] = useState({name: user?.name || '', email: user?.email || '', phone: '', address_line1: '', address_line2: '', city: '', state: '', postcode: '', country: 'IN', whatsapp_opt_in: false});
  const timerRef = useRef(null);

  // Load cancellation policy
  useEffect(() => {
    getPublicSettings().then(r => {
      setCancellationPolicy(r.data?.order_cancellation_policy || 'Orders cannot be cancelled or returned after confirmation via call or WhatsApp.');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    getPaymentConfig()
      .then(r => {
        setCheckoutEnabled(Boolean(r.data?.checkout_enabled));
        setLiveRatesAvailable(Boolean(r.data?.live_rates_available));
      })
      .catch(() => {
        setCheckoutEnabled(false);
        setLiveRatesAvailable(false);
      });
  }, []);

  // Countdown timer for price lock
  useEffect(() => {
    if (!priceLockExpiry) return;
    const tick = () => {
      const now = Date.now();
      const expiry = new Date(priceLockExpiry).getTime();
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setTimeLeft(diff);
      if (diff <= 0) {
        setPriceExpired(true);
        clearInterval(timerRef.current);
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [priceLockExpiry]);

  const formatTime = (s) => {
    if (s === null) return '';
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!user || !cart.items?.length) { setLoading(false); return; }
    const load = async () => {
      try {
        const details = await Promise.all(
          cart.items.map(async (item) => {
            const [coll, price] = await Promise.all([
              getCollectible(item.collectible_id).then(r => r.data),
              getCollectiblePrice(item.collectible_id).then(r => r.data).catch(e => ({ __error: e.response?.data?.detail || 'Price confirmation required' }))
            ]);
            return { ...item, collectible: coll, priceBreakdown: price?.__error ? null : price, priceError: price?.__error || '' };
          })
        );
        setCartItems(details);
      } catch { toast.error('Failed to load cart'); }
      finally { setLoading(false); }
    };
    load();
  }, [user, cart.items]);

  const handleRemove = async (collectibleId) => {
    setRemovingId(collectibleId);
    try {
      await removeFromCart(collectibleId);
      await refreshCart();
      setCartItems(prev => prev.filter(i => i.collectible_id !== collectibleId));
      toast.success('Removed from Chest');
    } catch { toast.error('Failed to remove'); }
    finally { setRemovingId(null); }
  };

  const hasFreshPrices = liveRatesAvailable && cartItems.every(item => Boolean(item.priceBreakdown?.final_payable));

  const handleCheckout = async () => {
    if (cartItems.length === 0 || priceExpired || !checkoutEnabled) return;
    if (!deliveryFormRef.current?.reportValidity()) return;
    const mobile = delivery.phone.replace(/[\s()-]/g, '');
    if (!/^(?:\+?91)?[6-9][0-9]{9}$/.test(mobile)) { toast.error('Enter a valid Indian mobile number'); return; }
    if (cartItems.length !== 1) { toast.error('Please keep one item in your Chest to pay.'); return; }
    const firstItem = cartItems[0];
    setCheckoutLoading(true);
    setCheckoutError('');
    setPriceExpired(false);
    let orderId = null;
    let releaseRequested = false;
    let paymentSubmitted = false;
    let razorpayInstance = null;

    const cancelUnpaidCheckout = async (reason, message) => {
      if (!orderId || releaseRequested || paymentSubmitted) return;
      releaseRequested = true;
      try {
        await cancelPaymentOrder(orderId, reason);
      } catch (error) {
        if (error.response?.status !== 409) console.error('Unable to cancel unpaid checkout');
      }
      setPriceLockExpiry(null);
      setTimeLeft(null);
      setCheckoutLoading(false);
      setCheckoutError(message);
      toast.error(message);
    };

    try {
      const paymentConfig = (await getPaymentConfig()).data;
      if (!paymentConfig.live_rates_available) throw new Error('Live Rate is unavailable. Checkout is disabled.');
      if (!paymentConfig.checkout_enabled || !paymentConfig.key_id?.startsWith('rzp_live_')) {
        throw new Error('Razorpay Live Checkout is not configured. Please try again later.');
      }
      await loadRazorpayCheckout();
      if (typeof window.Razorpay !== 'function') throw new Error('Razorpay Checkout could not initialize.');

      const lockRes = await lockCollectiblePrice(firstItem.collectible_id);
      const lockToken = lockRes.data.lock_token;
      if (lockRes.data.expires_at) setPriceLockExpiry(lockRes.data.expires_at);
      const checkoutRes = await initiateCheckout(firstItem.collectible_id, lockToken, delivery);
      orderId = checkoutRes.data.order_id;
      if (checkoutRes.data.price_lock_expiry) setPriceLockExpiry(checkoutRes.data.price_lock_expiry);
      const paymentRes = await createPaymentOrder(orderId);
      const rzpOrder = paymentRes.data;
      if (!rzpOrder.key_id?.startsWith('rzp_live_') || rzpOrder.key_id !== paymentConfig.key_id) {
        throw new Error('Razorpay Live Checkout validation failed.');
      }

      const options = {
        key: rzpOrder.key_id,
        amount: rzpOrder.amount_paise,
        currency: rzpOrder.currency || 'INR',
        name: 'SŌHM',
        description: `Order for ${firstItem.collectible?.name}`,
        order_id: rzpOrder.razorpay_order_id,
        handler: async (response) => {
          paymentSubmitted = true;
          try {
            await verifyPayment({ ...response, order_id: orderId });
            toast.success('Payment submitted. Waiting for confirmed payment status.');
            setPriceLockExpiry(null);
            setTimeLeft(null);
            await refreshCart();
            setCartItems([]);
            navigate(`/order-success/${orderId}`);
          } catch {
            const message = 'Payment status is still being confirmed. Check your Orders page before trying again.';
            setCheckoutError(message);
            toast.error(message);
          } finally {
            setCheckoutLoading(false);
          }
        },
        prefill: { name: delivery.name, email: delivery.email, contact: delivery.phone },
        theme: { color: '#1C1917' },
        modal: {
          ondismiss: () => {
            if (paymentSubmitted) setCheckoutLoading(false);
            else cancelUnpaidCheckout('razorpay_modal_dismissed', 'Checkout closed. Check your Orders page for payment status.');
          }
        }
      };

      razorpayInstance = new window.Razorpay(options);
      razorpayInstance.on('payment.failed', async () => {
        await cancelUnpaidCheckout('razorpay_payment_failed', 'Payment failed. Check your Orders page and bank before trying again.');
        razorpayInstance?.close?.();
      });
      razorpayInstance.open();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(e => e.msg).join('. ') : err.message || 'Checkout could not open. Please check your Orders page.';
      if (orderId) await cancelUnpaidCheckout('razorpay_initialization_failed', message);
      else {
        setCheckoutLoading(false);
        setCheckoutError(message);
        toast.error(message);
      }
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="max-w-lg mx-auto px-5 py-24 text-center">
          <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold mb-3">Your Chest</h2>
          <p className="text-muted-foreground mb-6">Sign in to view your items</p>
          <Link to="/login"><Button className="rounded-full px-8">Sign In</Button></Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="chest-page">
      <Navbar />
      <div className="grain-overlay" />

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-12">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2" data-testid="chest-title">Your Chest</h1>
        <p className="text-muted-foreground mb-10">{cartItems.length} {cartItems.length === 1 ? 'item' : 'items'}</p>

        {loading ? (
          <div className="space-y-4">
            {[1,2].map(i => (
              <div key={i} className="animate-pulse flex gap-4 p-4 bg-secondary/30 rounded-2xl">
                <div className="w-24 h-24 bg-secondary rounded-xl" />
                <div className="flex-1 space-y-3 py-2">
                  <div className="h-4 bg-secondary rounded w-1/2" />
                  <div className="h-3 bg-secondary rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : cartItems.length === 0 ? (
          <div className="text-center py-20">
            <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-display text-xl font-semibold mb-2">Chest is empty</h3>
            <p className="text-muted-foreground mb-6">Explore our collections and add pieces you love</p>
            <Link to="/gold"><Button className="rounded-full px-8" data-testid="shop-now-btn">Shop Gold</Button></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-10">
            {/* Items */}
            <div className="md:col-span-2 space-y-4">
              {cartItems.map((item, i) => (
                <motion.div
                  key={item.collectible_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="flex gap-4 p-4 bg-card rounded-2xl border border-border/40 "
                  data-testid={`cart-item-${item.collectible_id}`}
                >
                  <Link to={`/collectible/${item.collectible_id}`}>
                    <div className="w-20 h-20 md:w-24 md:h-24 rounded-xl overflow-hidden bg-secondary/30 flex-shrink-0">
                      <img
                        src={item.collectible?.image_url || item.collectible?.images?.[0] || ''}
                        alt={item.collectible?.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <Link to={`/collectible/${item.collectible_id}`} className="hover:text-gold transition-colors">
                      <h3 className="font-display font-semibold text-sm md:text-base truncate">{item.collectible?.name}</h3>
                    </Link>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.collectible?.metal_type === 'gold' ? 'Gold' : 'Silver'} {item.collectible?.weight ? `- ${item.collectible.weight}g` : ''}
                    </p>
                    <p className={`${MONEY_CLASSNAME} text-sm font-semibold mt-2`}>
                      {formatInr(item.priceBreakdown?.final_payable) || item.priceError || 'Live rate temporarily unavailable'}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(item.collectible_id)}
                    disabled={removingId === item.collectible_id}
                    className="text-muted-foreground hover:text-destructive transition-colors self-start p-1"
                    data-testid={`remove-${item.collectible_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>

            {/* Summary */}
            <div className="md:col-span-1">
              <div className="sticky top-28 bg-card rounded-2xl border border-white/5 p-6" data-testid="cart-summary">
                <h3 className="font-display font-semibold mb-5">Price Breakdown</h3>
                {!hasFreshPrices ? (
                  <p className="text-sm text-amber-400" data-testid="cart-rate-unavailable">{cartItems.find(item => item.priceError)?.priceError || 'Live rate temporarily unavailable'}</p>
                ) : (() => {
                  const agg = cartItems.reduce((a, item) => {
                    const pb = item.priceBreakdown;
                    if (!pb) return a;
                    const q = item.quantity || 1;
                    return {
                      supplier_cost: a.supplier_cost + (pb.supplier_cost || 0) * q,
                      total_cost: a.total_cost + (pb.total_cost || 0) * q,
                      gateway_cost: a.gateway_cost + (pb.gateway_cost || 0) * q,
                      customer_delivery_cost: a.customer_delivery_cost + (pb.customer_delivery_cost || 0) * q,
                      profit: a.profit + (pb.projected_profit ?? pb.profit_13_percent ?? 0) * q,
                      gst: a.gst + (pb.gst || 0) * q,
                      final_price: a.final_price + (pb.final_payable || 0) * q,
                      gst_percent: pb.gst_percent || 3,
                    };
                  }, { supplier_cost:0, total_cost:0, gateway_cost:0, customer_delivery_cost:0, profit:0, gst:0, final_price:0, gst_percent:3 });
                  return (
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-muted-foreground">
                        <span>Supplier Cost</span>
                        <span className={MONEY_CLASSNAME} data-testid="cart-supplier-cost">{formatInr(agg.supplier_cost)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Separate delivery charge</span>
                        <span className={MONEY_CLASSNAME}>{formatInr(agg.customer_delivery_cost)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Payment Cost</span>
                        <span className={MONEY_CLASSNAME}>{formatInr(agg.gateway_cost)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Total Business Cost</span>
                        <span className={MONEY_CLASSNAME}>{formatInr(agg.total_cost)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>13% cost markup</span>
                        <span className={MONEY_CLASSNAME} data-testid="cart-margin">{formatInr(agg.profit)}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>GST ({agg.gst_percent}%)</span>
                        <span className={MONEY_CLASSNAME} data-testid="cart-gst">{formatInr(agg.gst)}</span>
                      </div>
                      <Separator className="!my-3 bg-white/5" />
                      <div className="flex justify-between font-bold text-base pt-1">
                        <span>Total</span>
                        <span className={`${MONEY_CLASSNAME} text-gold`} data-testid="cart-final-price">{formatInr(agg.final_price)}</span>
                      </div>
                    </div>
                  );
                })()}
                <form ref={deliveryFormRef} onSubmit={e => e.preventDefault()} className="mt-5 space-y-3" aria-label="Delivery details">
                  <h3 className="font-semibold">Delivery details</h3>
                  {[["name", "Recipient name", "text"], ["email", "Order email", "email"], ["phone", "Indian mobile number", "tel"], ["address_line1", "Street address", "text"], ["address_line2", "Apartment / landmark (optional)", "text"], ["city", "City", "text"], ["state", "State", "text"], ["postcode", "PIN code", "text"]].map(([key, label, type]) => (
                    <label key={key} className="block text-sm">{label}
                      <input name={key} type={type} required={key !== 'address_line2'} minLength={key === 'address_line1' ? 5 : key === 'name' || key === 'city' || key === 'state' ? 2 : undefined} maxLength={key === 'postcode' ? 6 : key === 'phone' ? 18 : 200} pattern={key === 'postcode' ? '[1-9][0-9]{5}' : undefined} value={delivery[key]} onChange={e => setDelivery(prev => ({...prev, [key]: e.target.value}))} className="mt-1 block w-full rounded border border-border bg-background px-3 py-2" />
                    </label>
                  ))}
                  <p className="text-xs text-muted-foreground">Delivery within India. These details are saved with your order.</p>
                  <label className="flex gap-2 text-xs"><input type="checkbox" checked={delivery.whatsapp_opt_in} onChange={e => setDelivery(prev => ({...prev, whatsapp_opt_in: e.target.checked}))} />I agree to receive order updates on WhatsApp when available.</label>
                  {cartItems.length > 1 && <p role="alert" className="text-sm text-amber-500">Checkout currently supports one item. Remove other items before paying.</p>}
                </form>
                {/* Price Lock Timer */}
                {priceLockExpiry && !priceExpired && timeLeft !== null && (
                  <div className="flex items-center justify-center gap-2 text-sm mt-4 text-yellow-400" data-testid="price-lock-timer">
                    <Clock className="w-4 h-4" />
                    <span>Price locked for <strong className="font-mono">{formatTime(timeLeft)}</strong></span>
                  </div>
                )}
                {priceExpired && (
                  <div className="flex items-center justify-center gap-2 text-sm mt-4 text-red-400" data-testid="price-expired-warning">
                    <AlertTriangle className="w-4 h-4" />
                    <span>Price expired &mdash; refresh to get new rates</span>
                  </div>
                )}
                
                {/* Non-Cancellable Order Warning */}
                <div className="mt-5 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="cancellation-warning">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-400/90 leading-relaxed">
                      {cancellationPolicy || 'Orders cannot be cancelled or returned after confirmation via call or WhatsApp.'}
                    </p>
                  </div>
                </div>

                <Button
                  onClick={priceExpired ? () => window.location.reload() : handleCheckout}
                  disabled={checkoutLoading || cartItems.length === 0 || !checkoutEnabled || !hasFreshPrices}
                  className={`w-full rounded-full h-12 text-sm font-semibold uppercase tracking-wide mt-4 ${priceExpired ? 'bg-red-500/80 text-white hover:bg-red-500' : 'bg-foreground text-background hover:bg-foreground/90'}`}
                  data-testid="checkout-btn"
                >
                  {priceExpired ? (
                    <><AlertTriangle className="w-4 h-4 mr-2" /> Refresh Prices</>
                  ) : (
                    <><CreditCard className="w-4 h-4 mr-2" /> {checkoutLoading ? 'Processing...' : checkoutEnabled && hasFreshPrices ? 'Checkout' : liveRatesAvailable ? 'Checkout Unavailable' : 'Live Rate Unavailable'}</>
                  )}
                </Button>
                {checkoutError && <p className="text-xs text-red-400 mt-3" role="alert" data-testid="checkout-error">{checkoutError}</p>}
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
